/**
 * MediaPlayer: how images, audio and video appear on the board (and in the
 * library preview, which shows exactly what the board will show).
 *
 * - Image: grows in softly; click opens a zoom that flies from the image to
 *   full screen and back (FLIP with the Web Animations API).
 * - Audio: a big round play button with a progress ring and a row of bars
 *   that move with the real sound (Web Audio analyser), time display.
 * - Video: custom controls in the forest style, big play button while
 *   paused, controls that fade away during playback, scrubbing.
 *
 * Module board views create a player and call `play()`, `pause()` and
 * `restart()` when the teacher's intents say so.
 *
 * Browsers refuse to play sound until someone has interacted with the page.
 * The board page calls `armAudioUnlock()` once; the first click or key on
 * the board unlocks sound for the rest of the session. If a play is refused
 * before that, the player shows a "Ton einschalten" hint instead of failing
 * silently.
 */
import { esc } from '../dom';
import { formatDuration } from '../media';
import './media-player.css';

export type PlayableKind = 'image' | 'audio' | 'video';

export interface MediaPlayerOptions {
  kind: PlayableKind;
  url: string;
  posterUrl?: string | null;
  /** Accessible description (file name or question context). */
  label?: string;
  /** Smaller variant for previews inside dialogs. */
  compact?: boolean;
}

export interface MediaPlayer {
  readonly element: HTMLElement;
  play(): Promise<void>;
  pause(): void;
  restart(): Promise<void>;
  readonly playing: boolean;
  destroy(): void;
}

/* ------------------------------------------------------------------------ */
/* Volume and sound unlock, shared by all players on the page                */
/* ------------------------------------------------------------------------ */

const VOLUME_KEY = 'schulspiele.volume';

export function storedVolume(): number {
  try {
    const v = Number(localStorage.getItem(VOLUME_KEY));
    return Number.isFinite(v) && v > 0 && v <= 1 ? v : 1;
  } catch {
    return 1;
  }
}

function storeVolume(v: number): void {
  try {
    localStorage.setItem(VOLUME_KEY, String(v));
  } catch {
    // Private mode: volume just is not remembered.
  }
}

let audioContext: AudioContext | null = null;
let unlocked = false;
const unlockListeners = new Set<() => void>();

function context(): AudioContext | null {
  if (audioContext !== null) return audioContext;
  try {
    audioContext = new AudioContext();
  } catch {
    audioContext = null;
  }
  return audioContext;
}

/** Whether a user gesture has unlocked sound on this page. */
export function audioUnlocked(): boolean {
  return unlocked;
}

/** Call once on pages that play sound without a direct click (the board). */
export function armAudioUnlock(onUnlock?: () => void): () => void {
  if (onUnlock !== undefined) unlockListeners.add(onUnlock);
  const unlock = (): void => {
    void context()?.resume();
    unlocked = true;
    for (const l of unlockListeners) l();
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };
  if (!unlocked) {
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
  }
  return () => {
    if (onUnlock !== undefined) unlockListeners.delete(onUnlock);
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };
}

const reducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------------ */
/* Icons                                                                     */
/* ------------------------------------------------------------------------ */

// Play and pause drawn with the same number of points so `d` can morph.
const PLAY_D = 'M8 5 L19 12 L19 12 L8 19 Z M8 5 L8 5 L8 19 L8 19 Z';
const PAUSE_D = 'M6 5 L10 5 L10 19 L6 19 Z M14 5 L18 5 L18 19 L14 19 Z';

function playIcon(): string {
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><path class="mp-icon" d="${PLAY_D}"/></svg>`;
}

const RESTART_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 1 0 2.4-5.7M4 4v5h5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function setIcon(button: HTMLElement, playing: boolean): void {
  const path = button.querySelector<SVGPathElement>('.mp-icon');
  path?.setAttribute('d', playing ? PAUSE_D : PLAY_D);
  button.setAttribute('aria-label', playing ? 'Pause' : 'Abspielen');
  button.classList.toggle('is-playing', playing);
}

/* ------------------------------------------------------------------------ */

export function createMediaPlayer(options: MediaPlayerOptions): MediaPlayer {
  switch (options.kind) {
    case 'image':
      return imagePlayer(options);
    case 'audio':
      return audioPlayer(options);
    case 'video':
      return videoPlayer(options);
  }
}

/* ---- Image ------------------------------------------------------------------ */

function imagePlayer(options: MediaPlayerOptions): MediaPlayer {
  const el = document.createElement('figure');
  el.className = `mp mp--image ${options.compact === true ? 'mp--compact' : ''}`;
  el.innerHTML = `<img src="${esc(options.url)}" alt="${esc(options.label ?? '')}" decoding="async" /><span class="mp-zoom-hint" aria-hidden="true">⤢</span>`;
  const img = el.querySelector('img') as HTMLImageElement;
  img.addEventListener(
    'load',
    () => {
      el.classList.add('is-loaded');
    },
    { once: true },
  );
  if (img.complete) el.classList.add('is-loaded');

  let overlay: HTMLElement | null = null;
  const openZoom = (): void => {
    if (overlay !== null) return;
    const from = img.getBoundingClientRect();
    overlay = document.createElement('div');
    overlay.className = 'mp-lightbox';
    overlay.innerHTML = `<img src="${esc(options.url)}" alt="${esc(options.label ?? '')}" />`;
    document.body.append(overlay);
    const big = overlay.querySelector('img') as HTMLImageElement;
    const to = big.getBoundingClientRect();
    if (!reducedMotion()) {
      const dx = from.left + from.width / 2 - (to.left + to.width / 2);
      const dy = from.top + from.height / 2 - (to.top + to.height / 2);
      const s = from.width / Math.max(1, to.width);
      big.animate(
        [
          { transform: `translate(${String(dx)}px, ${String(dy)}px) scale(${String(s)})` },
          { transform: 'none' },
        ],
        { duration: 420, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' },
      );
      overlay.animate(
        [{ backgroundColor: 'rgb(22 41 31 / 0)' }, { backgroundColor: 'rgb(22 41 31 / 0.88)' }],
        {
          duration: 420,
          fill: 'forwards',
        },
      );
    }
    overlay.addEventListener('click', closeZoom);
  };
  const closeZoom = (): void => {
    const o = overlay;
    if (o === null) return;
    overlay = null;
    const big = o.querySelector('img') as HTMLImageElement;
    const from = big.getBoundingClientRect();
    const to = img.getBoundingClientRect();
    if (reducedMotion()) {
      o.remove();
      return;
    }
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const s = to.width / Math.max(1, from.width);
    big.animate(
      [
        { transform: 'none' },
        { transform: `translate(${String(dx)}px, ${String(dy)}px) scale(${String(s)})` },
      ],
      {
        duration: 320,
        easing: 'cubic-bezier(0.4, 0, 0.6, 1)',
        fill: 'forwards',
      },
    );
    o.animate(
      [{ backgroundColor: 'rgb(22 41 31 / 0.88)' }, { backgroundColor: 'rgb(22 41 31 / 0)' }],
      {
        duration: 320,
        fill: 'forwards',
      },
    ).finished.then(
      () => {
        o.remove();
      },
      () => {
        o.remove();
      },
    );
  };
  el.addEventListener('click', openZoom);
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') closeZoom();
  };
  window.addEventListener('keydown', onKey);

  return {
    element: el,
    play: () => Promise.resolve(),
    pause: () => undefined,
    restart: () => Promise.resolve(),
    get playing() {
      return false;
    },
    destroy() {
      window.removeEventListener('keydown', onKey);
      overlay?.remove();
      el.remove();
    },
  };
}

/* ---- Shared for audio and video ------------------------------------------- */

function soundBlockedHint(host: HTMLElement, retry: () => void): void {
  if (host.querySelector('.mp-blocked') !== null) return;
  const hint = document.createElement('button');
  hint.type = 'button';
  hint.className = 'mp-blocked';
  hint.textContent = 'Ton einschalten: einmal hier klicken';
  hint.addEventListener('click', (e) => {
    e.stopPropagation();
    unlocked = true;
    void context()?.resume();
    hint.remove();
    retry();
  });
  host.append(hint);
}

async function tryPlay(media: HTMLMediaElement, host: HTMLElement): Promise<void> {
  try {
    await media.play();
    host.querySelector('.mp-blocked')?.remove();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'NotAllowedError') {
      soundBlockedHint(host, () => void tryPlay(media, host));
    }
  }
}

function volumeControl(media: HTMLMediaElement): string {
  media.volume = storedVolume();
  return `<label class="mp-volume" title="Lautstärke"><span aria-hidden="true">🔈</span><span class="sr-only">Lautstärke</span><input type="range" min="0.05" max="1" step="0.05" value="${String(media.volume)}" /></label>`;
}

function wireVolume(root: HTMLElement, media: HTMLMediaElement): void {
  const input = root.querySelector<HTMLInputElement>('.mp-volume input');
  input?.addEventListener('input', () => {
    media.volume = Number(input.value);
    storeVolume(media.volume);
  });
  input?.addEventListener('click', (e) => {
    e.stopPropagation();
  });
}

/* ---- Audio ------------------------------------------------------------------ */

const BAR_COUNT = 28;

function audioPlayer(options: MediaPlayerOptions): MediaPlayer {
  const el = document.createElement('div');
  el.className = `mp mp--audio ${options.compact === true ? 'mp--compact' : ''}`;
  const audio = new Audio();
  audio.preload = 'auto';
  audio.src = options.url;
  const R = 46;
  const C = 2 * Math.PI * R;
  el.innerHTML = `
    <button type="button" class="mp-audio__play" aria-label="Abspielen">
      <svg class="mp-ring" viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r="${String(R)}" class="mp-ring__track" />
        <circle cx="50" cy="50" r="${String(R)}" class="mp-ring__fill" stroke-dasharray="${String(C)}" stroke-dashoffset="${String(C)}" />
      </svg>
      <span class="mp-audio__icon">${playIcon()}</span>
    </button>
    <div class="mp-audio__side">
      <div class="mp-bars" aria-hidden="true">${'<span></span>'.repeat(BAR_COUNT)}</div>
      <div class="mp-audio__meta">
        <span class="mp-time">0:00</span>
        <button type="button" class="mp-small" data-restart aria-label="Von vorn">${RESTART_SVG}</button>
        ${volumeControl(audio)}
      </div>
    </div>
    <span class="sr-only">${esc(options.label ?? 'Tonaufnahme')}</span>`;
  wireVolume(el, audio);

  const button = el.querySelector('.mp-audio__play') as HTMLButtonElement;
  const ring = el.querySelector('.mp-ring__fill') as SVGCircleElement;
  const bars = [...el.querySelectorAll<HTMLElement>('.mp-bars span')];
  const time = el.querySelector('.mp-time') as HTMLElement;

  let analyser: AnalyserNode | null = null;
  let data: Uint8Array<ArrayBuffer> | null = null;
  let frame = 0;

  const ensureAnalyser = (): void => {
    if (analyser !== null) return;
    const ctx = context();
    if (ctx === null) return;
    try {
      const source = ctx.createMediaElementSource(audio);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.75;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      data = new Uint8Array(analyser.frequencyBinCount);
    } catch {
      analyser = null; // falls back to the CSS animation
    }
  };

  const draw = (): void => {
    const progress = audio.duration > 0 ? audio.currentTime / audio.duration : 0;
    ring.style.strokeDashoffset = String(C * (1 - progress));
    time.textContent = `${formatDuration(audio.currentTime * 1000)} / ${formatDuration((audio.duration || 0) * 1000)}`;
    if (analyser !== null && data !== null && !reducedMotion()) {
      analyser.getByteFrequencyData(data);
      bars.forEach((bar, i) => {
        const v =
          data?.[Math.min(data.length - 1, Math.floor((i / bars.length) * data.length * 0.8))] ?? 0;
        bar.style.transform = `scaleY(${String(0.12 + (v / 255) * 0.88)})`;
      });
    }
    if (!audio.paused) frame = requestAnimationFrame(draw);
  };

  const sync = (): void => {
    const playing = !audio.paused && !audio.ended;
    setIcon(button, playing);
    el.classList.toggle('is-playing', playing);
    el.classList.toggle('has-analyser', analyser !== null);
    cancelAnimationFrame(frame);
    if (playing) frame = requestAnimationFrame(draw);
    else {
      draw();
      bars.forEach((b) => (b.style.transform = ''));
    }
  };
  for (const ev of ['play', 'pause', 'ended', 'loadedmetadata', 'seeked'])
    audio.addEventListener(ev, sync);

  const play = async (): Promise<void> => {
    ensureAnalyser();
    await context()
      ?.resume()
      .catch(() => undefined);
    await tryPlay(audio, el);
  };
  button.addEventListener('click', () => {
    if (audio.paused) void play();
    else audio.pause();
  });
  el.querySelector('[data-restart]')?.addEventListener('click', () => {
    audio.currentTime = 0;
    void play();
  });

  return {
    element: el,
    play,
    pause: () => {
      audio.pause();
    },
    restart: async () => {
      audio.currentTime = 0;
      await play();
    },
    get playing() {
      return !audio.paused;
    },
    destroy() {
      cancelAnimationFrame(frame);
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      el.remove();
    },
  };
}

/* ---- Video ------------------------------------------------------------------ */

function videoPlayer(options: MediaPlayerOptions): MediaPlayer {
  const el = document.createElement('div');
  el.className = `mp mp--video ${options.compact === true ? 'mp--compact' : ''}`;
  const video = document.createElement('video');
  video.preload = 'auto';
  video.playsInline = true;
  video.src = options.url;
  if (options.posterUrl != null) video.poster = options.posterUrl;
  if (options.label !== undefined) video.setAttribute('aria-label', options.label);
  el.append(video);
  el.insertAdjacentHTML(
    'beforeend',
    `<button type="button" class="mp-video__big" aria-label="Abspielen">${playIcon()}</button>
     <div class="mp-video__bar">
       <button type="button" class="mp-small" data-toggle aria-label="Abspielen">${playIcon()}</button>
       <button type="button" class="mp-small" data-restart aria-label="Von vorn">${RESTART_SVG}</button>
       <div class="mp-track" role="slider" tabindex="0" aria-label="Position" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><div class="mp-track__fill"></div><div class="mp-track__knob"></div></div>
       <span class="mp-time">0:00</span>
       ${volumeControl(video)}
     </div>`,
  );
  wireVolume(el, video);

  const big = el.querySelector('.mp-video__big') as HTMLButtonElement;
  const toggle = el.querySelector('[data-toggle]') as HTMLButtonElement;
  const track = el.querySelector('.mp-track') as HTMLElement;
  const fill = el.querySelector('.mp-track__fill') as HTMLElement;
  const knob = el.querySelector('.mp-track__knob') as HTMLElement;
  const time = el.querySelector('.mp-time') as HTMLElement;

  let frame = 0;
  let idleTimer = 0;
  const draw = (): void => {
    const p = video.duration > 0 ? video.currentTime / video.duration : 0;
    fill.style.transform = `scaleX(${String(p)})`;
    knob.style.left = `${String(p * 100)}%`;
    track.setAttribute('aria-valuenow', String(Math.round(p * 100)));
    time.textContent = `${formatDuration(video.currentTime * 1000)} / ${formatDuration((video.duration || 0) * 1000)}`;
    if (!video.paused) frame = requestAnimationFrame(draw);
  };
  const sync = (): void => {
    const playing = !video.paused && !video.ended;
    setIcon(toggle, playing);
    setIcon(big, playing);
    el.classList.toggle('is-playing', playing);
    cancelAnimationFrame(frame);
    draw();
    if (playing) frame = requestAnimationFrame(draw);
    wake();
  };
  // Controls fade out while playing and nobody moves the mouse.
  const wake = (): void => {
    el.classList.remove('is-idle');
    window.clearTimeout(idleTimer);
    if (!video.paused)
      idleTimer = window.setTimeout(() => {
        el.classList.add('is-idle');
      }, 2500);
  };
  for (const ev of ['play', 'pause', 'ended', 'loadedmetadata', 'seeked'])
    video.addEventListener(ev, sync);
  el.addEventListener('pointermove', wake);

  const play = (): Promise<void> => tryPlay(video, el);
  const togglePlay = (): void => {
    if (video.paused) void play();
    else video.pause();
  };
  big.addEventListener('click', togglePlay);
  toggle.addEventListener('click', togglePlay);
  video.addEventListener('click', togglePlay);
  el.querySelector('[data-restart]')?.addEventListener('click', () => {
    video.currentTime = 0;
    void play();
  });

  const seekTo = (clientX: number): void => {
    const r = track.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    if (video.duration > 0) video.currentTime = p * video.duration;
    draw();
  };
  track.addEventListener('pointerdown', (e) => {
    track.setPointerCapture(e.pointerId);
    seekTo(e.clientX);
    const move = (m: PointerEvent): void => {
      seekTo(m.clientX);
    };
    track.addEventListener('pointermove', move);
    track.addEventListener(
      'pointerup',
      () => {
        track.removeEventListener('pointermove', move);
      },
      {
        once: true,
      },
    );
  });
  track.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') video.currentTime = Math.min(video.duration, video.currentTime + 5);
    if (e.key === 'ArrowLeft') video.currentTime = Math.max(0, video.currentTime - 5);
  });

  return {
    element: el,
    play,
    pause: () => {
      video.pause();
    },
    restart: async () => {
      video.currentTime = 0;
      await play();
    },
    get playing() {
      return !video.paused;
    },
    destroy() {
      cancelAnimationFrame(frame);
      window.clearTimeout(idleTimer);
      video.pause();
      video.removeAttribute('src');
      video.load();
      el.remove();
    },
  };
}
